import { useEffect, useState } from 'react';
import { api, isAuthError, authErrorMessage } from '../lib/api';
import type { PaymentAttempt } from '../types/payments';

export const ATTEMPT_POLL_INTERVAL_MS = 2000;
/** After this many consecutive failed polls, surface the connectivity warning. */
const POLL_FAILURE_THRESHOLD = 3;

interface AttemptPollerState {
  /** Latest attempt snapshot from the service; null until the first poll lands. */
  attempt: PaymentAttempt | null;
  /** Connectivity warning — the wait itself continues regardless. */
  pollError: string | null;
}

/**
 * Poll `GET /api/payments/attempt/:id` while a payment waits on the operator's
 * 2FA approval (CHO-754). Starts immediately, then every 2 s, and stops as
 * soon as `attemptId` becomes null (terminal state reached). Poll failures
 * never abort the wait — the service may simply be restarting — but are
 * surfaced after three consecutive misses so the panel never looks silently
 * frozen.
 *
 * CHO-759 review: that read route is now behind the shared-secret gate, and
 * every unauthenticated poll is a failed sign-in attempt on the rate limiter
 * that `POST /api/auth/login` also checks. An auth-class response (401/429)
 * therefore stops the polling outright — a stale open wait panel must never
 * poll the operator's own login into the 24 h lockout — and is surfaced as
 * the auth problem it actually is, never as lost connectivity.
 */
export function useAttemptPoller(attemptId: string | null): AttemptPollerState {
  const [attempt, setAttempt] = useState<PaymentAttempt | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  // Reset when a different attempt starts — state adjusted during render, not
  // in an effect (react-hooks/set-state-in-effect).
  const [polledId, setPolledId] = useState(attemptId);
  if (polledId !== attemptId) {
    setPolledId(attemptId);
    setAttempt(null);
    setPollError(null);
  }

  useEffect(() => {
    if (!attemptId) return;

    let cancelled = false;
    let failures = 0;
    let interval: ReturnType<typeof setInterval> | null = null;

    async function poll() {
      try {
        const latest = await api.getPaymentAttempt(attemptId as string);
        if (cancelled) return;
        failures = 0;
        setPollError(null);
        setAttempt(latest);
      } catch (err) {
        if (cancelled) return;
        if (isAuthError(err)) {
          if (interval) clearInterval(interval);
          setPollError(authErrorMessage(err));
          return;
        }
        failures += 1;
        if (failures >= POLL_FAILURE_THRESHOLD) {
          setPollError('Lost contact with the payment service — still waiting for your approval. Keep this page open.');
        }
      }
    }

    poll();
    interval = setInterval(poll, ATTEMPT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [attemptId]);

  return { attempt, pollError };
}
